import { Switch } from "@mui/material";
import { MDBBtn, MDBInput, MDBTextArea, MDBTypography } from "mdb-react-ui-kit";
import { useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import useResumeIntroductionHandler from "../../context/resume/hooks/resume-introduction-handler";
import useResumeUserHandler from "../../context/resume/hooks/resume-user-handler";
import useResumePropertiesHandlers from "../../context/resume/hooks/resume-properties-handlers";
import { ResumeContext } from "../../context/resume/resume-context";
import { TranslationContext } from "../../context/translations/translations-context";
import { useCurrentUserRoles } from "../../hooks/useCurrentUserRoles";
import useResumeDocumentSection from "../../hooks/useResumeDocumentSection";
import useResumeProperties from "../../hooks/useResumeProperties";
import FileUploadForm from "../assets/FileUploadForm";
import { http } from "../services/httpService";
import { privateActionTypes } from "../../context/resume/reducer/resume-reducer";

export default function TopLeftSection() {
    const { displayLanguage, translations, changeLanguage } = useContext( TranslationContext );
    const { isCandidateRole, isManagerRole } = useCurrentUserRoles();

    const {
        resumeData,
        validationErrors,
        handleSave,
        dispatch,
    } = useContext( ResumeContext );

    const { documentLanguage, isAnonymous, isExperienceHidden, keywords, jobTitle } = useResumeProperties();
    const { introduction, user } = useResumeDocumentSection();
    const { handleChange } = useResumePropertiesHandlers();
    const { handleChange: handleDocumentIntroductionChange } = useResumeIntroductionHandler();
    const { handleChange: handleUserChange } = useResumeUserHandler();

    useEffect( () => {
        if ( documentLanguage && documentLanguage !== displayLanguage ) {
            changeLanguage( documentLanguage );
        }
    }, [ documentLanguage ] );
    const userId = resumeData.userId;
    const resumeId = resumeData.id;

    const history = useHistory();

    async function handleConfirmToManager() {

        await handleSave();

        try {
            const response = await http( `resumes/confirm-manager/${ userId }/${ resumeId }`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
            } );

            if ( !response.ok ) {
                console.error( `Confirmation au MANAGER a échoué, status: ${ response.status }` );
                toast.error( `Confirmation au MANAGER a échoué, status: ${ response.status }` );
                return;
            }

            toast.success( "Confirmation au MANAGER réussie !" );
            history.push( '/mon-compte' );

        } catch ( error ) {
            console.error( `Erreur lors de la confirmation au MANAGER: ${ error }` );
            toast.error( "Une erreur s'est produite lors de la confirmation au MANAGER. Veuillez réessayer." );
        }
    }

    const handleFileUpload = ( extractionType, data ) => {
        if ( !data || !data.document ) {
            toast.error( "Aucune donnée structurée reçue" );
            return;
        }

        const mergedResume = {
            ...resumeData,
            document: {
                ...resumeData.document,
                ...data.document,
            },
            properties: {
                ...resumeData.properties,
                ...data.properties,
            }
        };

        dispatch( {
            type: privateActionTypes.SET_RESUME_DATA,
            payload: mergedResume,
        } );
        toast.success( "CV pré-rempli avec les données extraites" );
    };

    return (
        <div className="justify-content-center align-content-center text-center">
            <MDBTypography variant="h2" className="text-light pt-5 pb-3">
                { translations.topLeftSection.title }
            </MDBTypography>
            { isManagerRole && (
                <FileUploadForm onUploadData={ handleFileUpload } userId={ userId } resumeId={ resumeId }/>
            ) }
            { isManagerRole && (
                <div className="d-flex justify-content-center gap-3 pt-3 pb-2">
                    <div style={ { width: "300px" } }>
                        <MDBInput
                            type="text"
                            name="resumeFirstName"
                            label={ translations.topLeftSection.resumeFirstNameLabel }
                            className="text-light input-acv form-control-input"
                            contrast={ true }
                            value={ user?.firstName ?? "" }
                            onChange={ ( event ) => handleUserChange( "firstName", event.target.value ) }
                            onBlur={ ( event ) => handleUserChange( "firstName", event.target.value.trim() ) }
                        />
                    </div>
                    <div style={ { width: "300px" } }>
                        <MDBInput
                            type="text"
                            name="resumeLastName"
                            label={ translations.topLeftSection.resumeLastNameLabel }
                            className="text-light input-acv form-control-input"
                            contrast={ true }
                            value={ user?.lastName ?? "" }
                            onChange={ ( event ) => handleUserChange( "lastName", event.target.value ) }
                            onBlur={ ( event ) => handleUserChange( "lastName", event.target.value.trim() ) }
                        />
                    </div>
                </div>
            ) }
            <div
                className="justify-content-center pt-3"
                style={ { display: "flex", alignItems: "center", color: "white" } }
            >
                <p>{ translations.topLeftSection.anonymousLabel }</p>
                <Switch
                    name="isAnonymous"
                    checked={ isAnonymous }
                    onChange={ ( e, checked ) => handleChange( 'isAnonymous', checked ) }
                    className="mb-3"
                />
                { isManagerRole && (
                    <>
                        <p>{ translations.topLeftSection.hideExperienceLabel }</p>
                        <Switch
                            name="isExperienceHidden"
                            checked={ isExperienceHidden }
                            onChange={ ( e, checked ) => handleChange( 'isExperienceHidden', checked ) }
                            className="mb-3"
                        />
                    </>
                ) }
            </div>
            { isManagerRole ? (
                <div className="text-center mb-4">
                    <MDBBtn type="submit" className="btn_primary" onClick={ handleSave } style={ { width: "250px" } }>
                        { translations.topLeftSection.saveAsDraftLabel }
                    </MDBBtn>
                </div>
            ) : isCandidateRole && (
                <div className="d-flex justify-content-center mt-4 mb-3 gap-2">
                    <MDBBtn type="submit" className="btn_primary" onClick={ handleSave } style={ { width: "250px" } }>
                        { translations.topLeftSection.saveAsDraftLabel }
                    </MDBBtn>
                    <MDBBtn type="submit" className="btn_primary" onClick={ handleConfirmToManager }
                            style={ { width: "530px" } }>
                        { translations.topLeftSection.saveAndNotifyManagerLabel }
                    </MDBBtn>
                </div>
            ) }
            <div className="d-flex justify-content-between gap-2">
                {/* Document language input */ }
                <div>
                    <select
                        className={ `form-select ${ validationErrors?.documentLanguage ? 'border-danger' : '' }` }
                        name="documentLanguage"
                        onChange={ ( e ) => handleChange( 'documentLanguage', e.target.value ) }
                        value={ documentLanguage }
                    >
                        <option value="">{ translations.topLeftSection.resumeLanguageLabel }</option>
                        <option value="fr">{ translations.general.french }</option>
                        <option value="en">{ translations.general.english }</option>
                    </select>
                    { validationErrors?.documentLanguage && (
                        <small>
                            <i className="fas fa-exclamation-triangle text-danger"></i> <span
                            className="text-white">{ validationErrors.documentLanguage }</span>
                        </small>
                    ) }
                </div>
                {/* JobTitle input */ }
                <div>
                    <div className="input-icon-container">
                        <MDBInput
                            type="text"
                            name="jobTitle"
                            label={ translations.topLeftSection.jobTitleLabel }
                            placeholder={ translations.topLeftSection.jobTitlePlaceholder }
                            className="text-light input-acv form-control-input"
                            contrast={ true }
                            value={ jobTitle }
                            onChange={ ( e ) => handleChange( 'jobTitle', e.target.value ) }
                        />
                        <span title={ translations.topLeftSection.jobTitleTooltip } className="info-icon">
                            <i className="fas fa-info-circle ms-1"></i>
                        </span>
                    </div>
                </div>
                {/* Keywords input */ }
                <div>
                    <div className="input-icon-container">
                        <MDBInput
                            type="text"
                            name="keywords"
                            label={ translations.topLeftSection.resumeNameLabel }
                            placeholder="Ex: Front-end"
                            className={ `text-light input-acv form-control-input ${ validationErrors?.keywords
                                ? 'border-danger' : '' }` }
                            contrast={ true }
                            value={ keywords.join( ', ' ) }
                            onChange={ ( e ) =>
                                handleChange( 'keywords', e.target.value
                                    .split( ', ' )
                                ) }
                        />
                        <span title="Définissez une mot clé au CV." className="info-icon">
                            <i className="fas fa-info-circle"></i>
                        </span>
                    </div>
                    <div>
                        { validationErrors?.keywords && (
                            <small>
                                <i className="fas fa-exclamation-triangle text-danger"></i><span
                                className="text-white">{ validationErrors.keywords }</span>
                            </small>
                        ) }
                    </div>
                </div>
            </div>
            {/* Introduction input */ }
            <div>
                <MDBTextArea
                    name="introduction"
                    label={ translations.topLeftSection.introductionLabel }
                    className={ `mt-3 form-control-input ${ validationErrors?.introduction ? 'border-danger' : '' }` }
                    rows={ 2 }
                    contrast={ true }
                    value={ introduction }
                    onChange={ ( e ) => handleDocumentIntroductionChange( e.target.value ) }
                />
                { validationErrors?.introduction && (
                    <small>
                        <i className="fas fa-exclamation-triangle text-danger"></i>{ ' ' }
                        <span className="text-white">{ validationErrors.introduction }</span>
                    </small>
                ) }
            </div>
        </div>
    );
}
